import React, { useState, useEffect, useRef } from 'react'
import { toast } from 'react-hot-toast'
import { api } from '../lib/api'
import { tomTtsModel, tomTtsVoice } from '../lib/tomTtsDefaults'

interface HelpText {
  id: number
  key: string
  title: string
  content: string
  category: string
  page?: string
  component?: string
  sort_order: number
}

interface UserHelpSettings {
  user_id: number
  tts_enabled: boolean
  tts_muted: boolean
  preferred_voice: string
  preferred_language: string
  auto_play_help: boolean
}

interface HelpButtonProps {
  helpKey: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'primary' | 'secondary'
  showText?: boolean
  text?: string
}

const HelpButton: React.FC<HelpButtonProps> = ({
  helpKey,
  className = '',
  size = 'md',
  variant = 'default',
  showText = false,
  text = 'Hilfe'
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [helpText, setHelpText] = useState<HelpText | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [userSettings, setUserSettings] = useState<UserHelpSettings | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Session Storage für Mute-Zustand
  const MUTE_STORAGE_KEY = 'help_tts_muted'

  useEffect(() => {
    // Mute-Zustand aus Session Storage laden
    const storedMuteState = sessionStorage.getItem(MUTE_STORAGE_KEY)
    if (storedMuteState !== null) {
      const parsedMuteState = JSON.parse(storedMuteState)
      console.log('💾 Loading mute state from session storage:', parsedMuteState)
      setIsMuted(parsedMuteState)
    }
  }, [])

  // Automatische Audio-Wiedergabe beim Öffnen des Modals
  useEffect(() => {
    if (isOpen && helpText && !isMuted && !isPlaying) {
      console.log('🎵 Auto-playing help audio on modal open', { 
        isOpen, 
        hasHelpText: !!helpText, 
        isMuted, 
        isPlaying,
        ttsEnabled: userSettings?.tts_enabled 
      })
      // Kleine Verzögerung, damit das Modal vollständig geöffnet ist
      const timer = setTimeout(() => {
        playAudio()
      }, 500)
      
      return () => clearTimeout(timer)
    }
  }, [isOpen, helpText, isMuted])

  useEffect(() => {
    // Mute-Zustand in Session Storage speichern
    console.log('💾 Saving mute state to session storage:', isMuted)
    sessionStorage.setItem(MUTE_STORAGE_KEY, JSON.stringify(isMuted))
  }, [isMuted])

  useEffect(() => {
    if (isOpen && !helpText) {
      loadHelpText()
    }
  }, [isOpen, helpText])

  useEffect(() => {
    if (isOpen && helpText && userSettings?.auto_play_help && !isMuted) {
      playAudio()
    }
  }, [isOpen, helpText, userSettings])

  const loadHelpText = async () => {
    setIsLoading(true)
    try {
      // Direkter API-Aufruf ohne Authentifizierung für Hilfe-Texte
      const response = await fetch(`/api/help/${helpKey}`)
      const data = await response.json()
      
      if (data.success) {
        setHelpText(data.data)
        console.log('Help text loaded from backend:', data.data.title)
      } else {
        // Fallback zu hartcodierten Hilfe-Texten
        setHelpText(getFallbackHelpText(helpKey))
        console.log('Using fallback help text for:', helpKey)
      }
    } catch (error: any) {
      console.error('Error loading help text:', error)
      // Fallback zu hartcodierten Hilfe-Texten
      setHelpText(getFallbackHelpText(helpKey))
      console.log('Using fallback help text due to error for:', helpKey)
    } finally {
      setIsLoading(false)
    }
  }

  const getFallbackHelpText = (key: string) => {
    const fallbackTexts: Record<string, any> = {
      'curricula.overview': {
        id: 0,
        key: 'curricula.overview',
        title: 'Curricula Übersicht',
        content: 'Hier können Sie alle verfügbaren Curricula verwalten. Sie können neue Curricula manuell erstellen oder PDF-Dokumente importieren, um automatisch Curricula und Kompetenzen zu extrahieren. Verwenden Sie Drag & Drop, um die Reihenfolge zu ändern.'
      },
      'curricula.create_manual': {
        id: 0,
        key: 'curricula.create_manual',
        title: 'Manuell erstellen',
        content: 'Klicken Sie auf "Manuell erstellen", um ein neues Curriculum ohne PDF-Import zu erstellen. Sie können später Kompetenzen und Inhaltsfelder hinzufügen.'
      },
      'curricula.pdf_import': {
        id: 0,
        key: 'curricula.pdf_import',
        title: 'PDF Import',
        content: 'Laden Sie ein PDF-Dokument hoch, um automatisch Curricula, Kompetenzen und Inhaltsfelder zu extrahieren. Das System verwendet KI, um die Struktur zu erkennen und zu importieren.'
      },
      'curricula.editor.overview': {
        id: 0,
        key: 'curricula.editor.overview',
        title: 'Curriculum Editor',
        content: 'Der Editor ermöglicht es Ihnen, Curricula und deren Kompetenzen zu bearbeiten. Verwenden Sie die Tabs, um zwischen Kompetenzen, Inhaltsfeldern und Mappings zu wechseln.'
      },
      'curricula.editor.competencies': {
        id: 0,
        key: 'curricula.editor.competencies',
        title: 'Kompetenzen',
        content: 'Verwalten Sie hier die Kompetenzen Ihres Curriculums. Sie können neue Kompetenzen hinzufügen, bearbeiten oder löschen. Verwenden Sie Drag & Drop, um die Struktur zu ändern.'
      },
      'curricula.editor.content_fields': {
        id: 0,
        key: 'curricula.editor.content_fields',
        title: 'Inhaltsfelder',
        content: 'Inhaltsfelder beschreiben praktische Arbeitsbereiche. Hier können Sie konkrete Aufgaben, Experimente oder Projekte definieren.'
      },
      'curricula.editor.mappings': {
        id: 0,
        key: 'curricula.editor.mappings',
        title: 'Mappings',
        content: 'Mappings verbinden Kompetenzen mit Inhaltsfeldern. Dies zeigt, welche praktischen Arbeiten zu welchen Kompetenzen gehören.'
      }
    }
    
    return fallbackTexts[key] || {
      id: 0,
      key: key,
      title: 'Hilfe',
      content: 'Kein Hilfe-Text verfügbar für diesen Bereich.'
    }
  }

  const loadUserSettings = async () => {
    try {
      const response = await api.get('/help/user/settings')
      if (response.data.success) {
        setUserSettings(response.data.data)
      } else {
        // Fallback zu Standard-Einstellungen
        setUserSettings(getFallbackUserSettings())
      }
    } catch (error: any) {
      console.error('Error loading user settings:', error)
      // Fallback zu Standard-Einstellungen
      setUserSettings(getFallbackUserSettings())
    }
  }

  const getFallbackUserSettings = () => ({
    user_id: 0,
    tts_enabled: true,
    tts_muted: false,
    preferred_voice: 'Tom',
    preferred_language: 'de',
    auto_play_help: false
  })

  const playAudio = async () => {
    console.log('playAudio called:', { helpText: !!helpText, isMuted, ttsEnabled: userSettings?.tts_enabled })
    
    if (!helpText || isMuted) {
      console.log('Audio play blocked:', { helpText: !!helpText, isMuted, ttsEnabled: userSettings?.tts_enabled })
      return
    }

    try {
      setIsPlaying(true)
      console.log('Starting TTS with background generation...')
      
      // TTS mit Hintergrund-Generierung
      await playTTSWithBackgroundGeneration(helpText.content, helpText.id)
      
    } catch (error: any) {
      console.error('Error playing audio:', error)
      setIsPlaying(false)
      toast.error('Fehler beim Abspielen der Sprachausgabe')
    }
  }

  const playTTSWithBackgroundGeneration = async (text: string, helpTextId: number) => {
    try {
      // 1. Prüfen ob Audio-Datei bereits existiert und aktuell ist
      const audioStatus = await checkAudioStatus(helpTextId)
      
      if (audioStatus.hasAudio && audioStatus.isCurrent) {
        console.log('🎵 Using existing audio file')
        await playExistingAudioBlob(helpTextId)
        return
      }

      // 2. Direkte TTS-Generierung (da Hintergrund-Generierung Probleme hat)
      console.log('🔊 Generating TTS audio directly...')
      toast('Audio wird generiert...')
      
      // Direkte TTS-Generierung über /api/tts
      const uid = userSettings?.user_id != null ? String(userSettings.user_id) : ''
      const ttsUrl = `/api/tts?text=${encodeURIComponent(text)}&voice=${encodeURIComponent(
        tomTtsVoice()
      )}&model=${encodeURIComponent(tomTtsModel())}${
        uid ? `&hubUserId=${encodeURIComponent(uid)}` : ''
      }&v=${Date.now()}`
      console.log('🔊 TTS-URL:', ttsUrl)
      
      const audioResp = await fetch(ttsUrl, { cache: 'no-store' })
      if (!audioResp.ok) throw new Error(`TTS status ${audioResp.status}`)
      
      const audioBlob = await audioResp.blob()
      console.log('📊 Audio blob size:', audioBlob.size, 'bytes')
      
      // Prüfen ob es eine leere/ungültige Audio-Datei ist
      if (audioBlob.size <= 100) {
        console.log('⚠️ TTS returned empty/small audio file')
        toast.error('Audio-Generierung fehlgeschlagen - kein OpenAI API Key?')
        setIsPlaying(false)
        return
      }
      
      const audioUrl = URL.createObjectURL(audioBlob)
      const audio = new Audio(audioUrl)
      
      // Audio-Objekt in Ref speichern für Mute-Funktionalität
      audioRef.current = audio
      
      audio.onended = () => {
        console.log('✅ TTS audio playback completed')
        setIsPlaying(false)
        URL.revokeObjectURL(audioUrl)
        audioRef.current = null
      }
      
      audio.onerror = (error) => {
        console.error('❌ TTS audio playback error:', error)
        setIsPlaying(false)
        URL.revokeObjectURL(audioUrl)
        audioRef.current = null
        toast.error('Fehler beim Abspielen der Audio-Datei')
      }
      
      await audio.play()
      toast.success('Text wird vorgelesen')
      
    } catch (error) {
      console.error('TTS generation error:', error)
      setIsPlaying(false)
      toast.error('Fehler bei der Audio-Generierung')
    }
  }

  const checkAudioStatus = async (helpTextId: number): Promise<{hasAudio: boolean, isCurrent: boolean, audioPath?: string}> => {
    try {
      const response = await fetch(`/api/help/audio-status/${helpTextId}`)
      const data = await response.json()
      
      if (data.success) {
        return {
          hasAudio: data.data.hasAudio,
          isCurrent: data.data.isCurrent,
          audioPath: data.data.audioPath
        }
      }
      
      return { hasAudio: false, isCurrent: false }
    } catch (error) {
      console.error('Error checking audio status:', error)
      return { hasAudio: false, isCurrent: false }
    }
  }

  const playExistingAudioBlob = async (helpTextId: number) => {
    try {
      // Audio über die korrekte API-Route als Blob abrufen (funktioniert!)
      const audioUrl = `/api/help/audio/${helpTextId}`
      console.log('🎵 Playing cached audio from:', audioUrl)
      
      const response = await fetch(audioUrl)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const audioBlob = await response.blob()
      console.log('📊 Cached audio blob size:', audioBlob.size, 'bytes')
      
      const audioUrl2 = URL.createObjectURL(audioBlob)
      const audio = new Audio(audioUrl2)
      
      // Audio-Objekt in Ref speichern für Mute-Funktionalität
      audioRef.current = audio
      
      audio.onended = () => {
        console.log('✅ Cached audio playback completed')
        setIsPlaying(false)
        URL.revokeObjectURL(audioUrl2)
        audioRef.current = null
      }
      
      audio.onerror = (error) => {
        console.error('❌ Cached audio playback error:', error)
        setIsPlaying(false)
        URL.revokeObjectURL(audioUrl2)
        audioRef.current = null
        toast.error('Fehler beim Abspielen der Audio-Datei')
      }
      
      await audio.play()
      toast.success('Text wird vorgelesen (aus Cache)')
      
    } catch (error) {
      console.error('Error playing existing audio:', error)
      throw error
    }
  }

  const generateAudioInBackground = async (helpTextId: number, text: string) => {
    try {
      // Hintergrund-Generierung starten (nicht warten auf Antwort)
      fetch(`/api/help/generate-audio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          helpTextId: helpTextId,
          text: text
        })
      }).then(response => {
        if (response.ok) {
          console.log('✅ Background audio generation started')
        } else {
          console.error('❌ Background audio generation failed')
        }
      }).catch(error => {
        console.error('❌ Background audio generation error:', error)
      })
      
    } catch (error) {
      console.error('Error starting background generation:', error)
    }
  }

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    setIsPlaying(false)
  }

  const toggleMute = () => {
    const newMuteState = !isMuted
    console.log('🔇 Toggle mute:', { from: isMuted, to: newMuteState })
    setIsMuted(newMuteState)
    
    if (newMuteState && isPlaying) {
      console.log('🔇 Stopping audio due to mute')
      stopAudio()
    }
  }

  const handleOpen = async () => {
    console.log('HelpButton opened for key:', helpKey)
    // Standard-Einstellungen setzen, falls noch nicht geladen
    if (!userSettings) {
      console.log('Setting fallback user settings')
      setUserSettings(getFallbackUserSettings())
    }
    setIsOpen(true)
  }

  const handleClose = () => {
    stopAudio()
    setIsOpen(false)
  }

  // Größen-Klassen
  const sizeClasses = {
    sm: 'w-6 h-6 text-sm',
    md: 'w-8 h-8 text-base',
    lg: 'w-10 h-10 text-lg'
  }

  // Variant-Klassen
  const variantClasses = {
    default: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
    primary: 'bg-primary-500 hover:bg-primary-600 text-white',
    secondary: 'bg-secondary-500 hover:bg-secondary-600 text-white'
  }

  return (
    <>
      <button
        onClick={handleOpen}
        className={`
          ${sizeClasses[size]} 
          ${variantClasses[variant]}
          ${className}
          rounded-full flex items-center justify-center
          transition-colors duration-200
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        `}
        title="Hilfe anzeigen"
      >
        {showText ? (
          <span className="flex items-center gap-1">
            <span>?</span>
            <span className="text-xs">{text}</span>
          </span>
        ) : (
          <span>?</span>
        )}
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-900">
                  {helpText?.title || 'Hilfe'}
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleMute}
                    className={`
                      p-1 rounded-full transition-colors
                      ${isMuted 
                        ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                        : 'bg-green-100 text-green-600 hover:bg-green-200'
                      }
                    `}
                    title={isMuted ? 'Stumm schalten' : 'Laut schalten'}
                  >
                      {isMuted ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.79L5.617 14H3a1 1 0 01-1-1V7a1 1 0 011-1h2.617l2.766-2.79a1 1 0 011-.134zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.79L5.617 14H3a1 1 0 01-1-1V7a1 1 0 011-1h2.617l2.766-2.79a1 1 0 011-.134zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    {isPlaying && (
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span>Spricht...</span>
                      </div>
                    )}
                  </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-96">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <span className="ml-2 text-gray-600">Lade Hilfe-Text...</span>
                </div>
              ) : helpText ? (
                <div className="prose prose-gray max-w-none">
                  <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {helpText.content}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Kein Hilfe-Text gefunden
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center gap-4">
                {!isMuted && (
                  <button
                    onClick={playAudio}
                    disabled={isPlaying || !helpText}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isPlaying ? (
                      <>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Pausiert
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.79L5.617 14H3a1 1 0 01-1-1V7a1 1 0 011-1h2.617l2.766-2.79a1 1 0 011-.134zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Vorlesen
                      </>
                    )}
                  </button>
                )}
              </div>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default HelpButton
